import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  CONFIRM,
  EMAIL,
  isUserInitialsValid,
  REGEXP_TEST,
  REQUIRED,
  REQUIRED_CHECKBOX,
  getValidationMessages,
} from '##/utils/validation';

export type FormValues = {
  username: string | null;
  email: string | null;
  password: string | null;
  re_password: string | null;
  name: string | null;
  user_agreement: boolean;
  partner_code: string | null;
};

export const useForm = () => {
  const [searchParams] = useSearchParams();

  const partner_code = searchParams.get('partner');

  const initialValues: FormValues = {
    username: null,
    email: null,
    password: null,
    re_password: null,
    user_agreement: false,
    partner_code,
    name: null,
  };

  const { t } = useTranslation();

  const schema = Yup.object().shape({
    username: REGEXP_TEST(t)(
      'login',
      /^([a-zA-Z0-9\-\\.]+)$/,
      t('errors.login.mask'),
    )
      .nullable()
      .required(getValidationMessages(t).required),
    email: REGEXP_TEST(t)(
      'email',
      /^[^!#$%*/?^`{|}~+]+@[^.]+\.[a-zA-Z]+$/,
      t('errors.email'),
    )
      .nullable()
      .required(getValidationMessages(t).required),
    password: REGEXP_TEST(t)(
      'password',
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
      t('errors.password.mask'),
    )
      .min(4, t('errors.password.min')?.toString())
      .max(30, t('errors.password.max')?.toString())
      .test(
        'password',
        t('errors.password.username_exists_error')?.toString(),
        (value, context) => {
          const login: string = (context.parent.username || '').toLowerCase();
          const fieldValue: string = (value || '').toLowerCase();
          return !fieldValue.includes(login);
        },
      )
      .notOneOf(
        [Yup.ref('username')],
        t('errors.password.username_exists_error')?.toString(),
      )
      .nullable()
      .required(getValidationMessages(t).required),
    re_password: CONFIRM(t)()
      .nullable()
      .required(getValidationMessages(t).required),
    partner_code: Yup.lazy((value) => {
      if (value && value.trim().length > 0) {
        return Yup.string().length(40, t('errors.partner_code')?.toString());
      }
      return Yup.string().trim().length(0).nullable();
    }),
    user_agreement: REQUIRED_CHECKBOX(t)('user_agreement'),
    name: REQUIRED(t).test(
      'name',
      t('errors.name')?.toString(),
      isUserInitialsValid,
    ),
  });

  return {
    initialValues,
    schema,
  };
};
